import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { startAssessmentReview, getReviewStatus, pushReviewAssessment } from '../api/claudeApi';
import { getAdminQuestions } from '../api/underwritingApi';
import UnderwritingCopilot from './UnderwritingCopilot';

// How a scoring question's config tier moves the ARC score (mirrors AdminQuestionsModal).
const scoreEffectFor = (tier, boost, floor) => {
  switch (tier) {
    case 'terminal': return { label: 'Forces ARC 10', color: '#c0392b', bg: '#fdecec' };
    case 'distress': return { label: `+${boost ?? 1} · cap ${floor ?? 8}`, color: '#b8860b', bg: '#fdf3e2' };
    case 'crime': return { label: 'Compliance review only', color: '#2f57c4', bg: '#eaf0fd' };
    case 'graded': return { label: 'Feeds model score', color: '#1f9d55', bg: '#e8f6ee' };
    default: return { label: 'No score weight (context)', color: '#7a8398', bg: '#f0f2f7' };
  }
};
// Normalize a question for matching: drop the "(Answer No …)" disambiguation and punctuation.
const normQ = (s) => String(s || '').toLowerCase().replace(/\(answer no[^)]*\)/i, '').replace(/[^a-z0-9]+/g, ' ').trim();

const LOADING_STEPS = [
  { key: 'entity', label: 'Loading Entity' },
  { key: 'financials', label: 'Loading Financials' },
  { key: 'internal', label: 'Loading Internal Assessment' },
  { key: 'monitoring', label: 'Loading Monitoring Events' },
  { key: 'documents', label: 'Loading Documents' },
  { key: 'external', label: 'Loading External Intelligence' },
];

const REVIEW_STEPS = [
  'Reviewing Financial Statements',
  'Reviewing Credit Recommendation',
  'Reviewing Risk Factors',
  'Reviewing Industry',
  'Reviewing Public Information',
  'Reviewing Business Description',
  'Comparing Internal vs External',
  'Finalizing Review',
];

const markdownSx = {
  fontSize: 12.5,
  lineHeight: 1.6,
  color: '#3a4358',
  '& p': { m: 0, mb: 0.75 },
  '& ul, & ol': { m: 0, mb: 0.75, pl: 2.25 },
  '& li': { mb: 0.15 },
  '& h1, & h2, & h3, & h4': { m: 0, mb: 0.5, mt: 0.75, fontSize: 13, fontWeight: 700, color: '#1f2a44' },
  '& strong': { color: '#1f2a44' },
  '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 11.5, my: 0.75 },
  '& th, & td': { border: '0.5px solid #d5d9e4', px: 0.6, py: 0.3, textAlign: 'left' },
  '& th': { background: '#f0f2f7' },
  '& a': { color: '#2f57c4' },
};

const Md = ({ text }) => (
  <Box sx={markdownSx}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: (p) => <a {...p} target="_blank" rel="noopener noreferrer" /> }}>
      {text || ''}
    </ReactMarkdown>
  </Box>
);

const StepRow = ({ label, state }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
    {state === 'done' ? (
      <CheckCircleIcon sx={{ fontSize: 15, color: '#1f9d55' }} />
    ) : state === 'active' ? (
      <CircularProgress size={12} sx={{ color: '#2f57c4' }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: '#c7cedd' }} />
    )}
    <Typography sx={{ fontSize: 12, color: state === 'pending' ? '#a2aabb' : '#3a4358' }}>{label}</Typography>
  </Box>
);

const AssessmentReview = ({ tenantName, entityName, entity }) => {
  const reportKey = `crediarc:review:report:${entityName || 'entity'}`;
  const decisionsKey = `crediarc:review:decisions:${entityName || 'entity'}`;

  const [open, setOpen] = useState(false);
  // Resizable panel width — drag the left edge to enlarge. Persisted per browser.
  const WIDTH_KEY = 'crediarc:review:width';
  const MIN_W = 440;
  const clampWidth = (w) => Math.max(MIN_W, Math.min(w, Math.max(MIN_W, window.innerWidth - 80)));
  const [panelWidth, setPanelWidth] = useState(() => {
    const s = Number(localStorage.getItem(WIDTH_KEY));
    return s >= MIN_W ? s : 560;
  });
  const resizingRef = useRef(false);
  const startResize = useCallback((e) => {
    e.preventDefault();
    resizingRef.current = true;
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      setPanelWidth(clampWidth(window.innerWidth - clientX));
    };
    const onUp = () => {
      resizingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      try { localStorage.setItem(WIDTH_KEY, String(Math.round(panelWidthRef.current))); } catch (err) { /* ignore */ }
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, []);
  // Keep the latest width available to the mouseup handler without re-binding listeners.
  const panelWidthRef = useRef(panelWidth);
  useEffect(() => { panelWidthRef.current = panelWidth; }, [panelWidth]);

  const [running, setRunning] = useState(false);
  const [includeExternal, setIncludeExternal] = useState(true);
  const [error, setError] = useState(null);
  const [loadedChecklist, setLoadedChecklist] = useState({});
  const [reviewIdx, setReviewIdx] = useState(-1); // index of active reviewing step
  const [report, setReport] = useState(() => {
    try { return JSON.parse(localStorage.getItem(reportKey) || 'null'); } catch (e) { return null; }
  });
  const [decisions, setDecisions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(decisionsKey) || '{}'); } catch (e) { return {}; }
  });
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [scoringConfig, setScoringConfig] = useState([]); // admin critical-question config (tier/boost/floor)
  const timerRef = useRef(null);
  const runIdRef = useRef(0); // invalidates superseded runs / polling on re-run or unmount

  useEffect(() => {
    try { if (report) localStorage.setItem(reportKey, JSON.stringify(report)); } catch (e) { /* ignore */ }
  }, [reportKey, report]);
  useEffect(() => {
    try { localStorage.setItem(decisionsKey, JSON.stringify(decisions)); } catch (e) { /* ignore */ }
  }, [decisionsKey, decisions]);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  useEffect(() => () => { runIdRef.current += 1; stopTimer(); }, []);

  // Load the admin scoring-question config once the drawer opens, so each risk-question signal
  // can show its actual config weight (boost/tier) and how it moves the ARC score.
  useEffect(() => {
    if (!open || scoringConfig.length) return;
    let alive = true;
    (async () => {
      try {
        const data = await getAdminQuestions();
        if (alive && Array.isArray(data?.critical)) setScoringConfig(data.critical);
      } catch (e) { /* non-fatal — signals still render without the weight column */ }
    })();
    return () => { alive = false; };
  }, [open, scoringConfig.length]);

  // Match a signal's question text to its config entry (exact-normalized, else substring).
  const configForQuestion = useCallback((qText) => {
    if (!scoringConfig.length) return null;
    const n = normQ(qText);
    if (!n) return null;
    return (
      scoringConfig.find((c) => normQ(c.question) === n) ||
      scoringConfig.find((c) => { const cn = normQ(c.question); return cn && (cn.includes(n) || n.includes(cn)); }) ||
      null
    );
  }, [scoringConfig]);

  const runReview = useCallback(async () => {
    const myRun = ++runIdRef.current;
    setRunning(true);
    setError(null);
    setReport(null);
    setPushed(false);
    setLoadedChecklist({});
    setReviewIdx(-1);

    // Animate the reviewing steps while the background job runs.
    stopTimer();
    let idx = 0;
    timerRef.current = setInterval(() => {
      idx = Math.min(idx + 1, REVIEW_STEPS.length - 1);
      setReviewIdx(idx);
    }, 3500);

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      // Kick off the background job, then poll short requests until it finishes.
      const start = await startAssessmentReview({ tenantName, entityName, entity, allowWeb: includeExternal });
      if (runIdRef.current !== myRun) return;
      setLoadedChecklist(start?.checklist || {});
      setReviewIdx(0);
      const jobId = start?.jobId;
      if (!jobId) throw new Error('Review did not start');

      let result = null;
      for (let i = 0; i < 90; i++) { // ~6 min max at 4s intervals
        await sleep(4000);
        if (runIdRef.current !== myRun) return;
        const st = await getReviewStatus(jobId);
        if (st?.status === 'done') { result = st.report; break; }
        if (st?.status === 'error') throw new Error(st.message || 'Review failed');
        // else running — keep polling
      }
      if (!result) throw new Error('Review timed out. Please try again.');
      if (runIdRef.current !== myRun) return;
      stopTimer();
      setReviewIdx(REVIEW_STEPS.length);
      setReport(result);
    } catch (e) {
      if (runIdRef.current !== myRun) return;
      stopTimer();
      const status = e?.response?.status || e?.status;
      const msg = e?.response?.data?.message || e?.message;
      setError(status ? `Review failed (${status})${msg ? `: ${msg}` : ''}.` : msg || 'Review failed.');
    } finally {
      if (runIdRef.current === myRun) setRunning(false);
    }
  }, [tenantName, entityName, entity, includeExternal]);

  const handleOpen = () => {
    setOpen(true);
    if (!report && !running) runReview();
  };

  // Push the AI-suggested risk score, recommended limit and a short rationale onto the
  // company record. Sets it as the active recommendation (the CrediArc Auto block is kept).
  const handlePush = async () => {
    if (!report) return;
    const tenantId = entity?.tenantId;
    const companyId = entity?.companyId;
    if (!tenantId || !companyId) { toast.error('Missing tenant or company id — cannot push.'); return; }
    const sa = report.suggestedAssessment || {};
    // Full summary -> alternative-data box; a short summary of it -> the Rationale field.
    const summary = (sa.rationale || '').replace(/\s+/g, ' ').trim();
    const firstSentence = (summary.match(/^.*?[.!?](\s|$)/)?.[0] || summary).trim();
    const shortReason = ((sa.rationaleShort || firstSentence || sa.recommendation || '').replace(/\s+/g, ' ').trim().slice(0, 220)) || 'AI-suggested assessment';
    try {
      setPushing(true);
      const suggestedRevenue = report.suggestedRevenue?.valueUSD;
      const res = await pushReviewAssessment({
        tenantId,
        companyId,
        arcScore: sa.riskBand,
        recommendedLimit: sa.creditLimit,
        underwritingReason: shortReason,
        rationale: summary,
        suggestedRevenue,
      });
      setPushed(true);
      toast.success(res?.reUnderwriting
        ? 'Revenue updated — CrediArc is re-underwriting on it; the limit will refresh shortly.'
        : 'Suggested assessment pushed — refreshing…');
      // Refresh the page so the updated values show. (Re-underwrite runs in the background.)
      setTimeout(() => window.location.reload(), 1400);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(`Push failed${msg ? `: ${msg}` : ''}.`);
      setPushing(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<FactCheckIcon style={{ color: '#5186EC' }} />}
        style={{
          color: '#5186EC',
          textTransform: 'none',
          fontFamily: 'Rubik, sans-serif',
          minWidth: '50px',
          height: '36px',
          fontWeight: '250',
          whiteSpace: 'nowrap',
        }}
      >
        Review
      </Button>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: panelWidth }, maxWidth: '100vw', background: '#fbfcfe', overflow: 'visible' } }}
      >
        {/* Drag handle — grab and pull left to enlarge the review window. */}
        <Box
          onMouseDown={startResize}
          onTouchStart={startResize}
          sx={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: '8px',
            cursor: 'col-resize', zIndex: 2, display: { xs: 'none', sm: 'block' },
            '&:hover': { background: 'rgba(47,87,196,0.12)' },
            '&:hover .rz-grip': { background: '#2f57c4' },
          }}
          title="Drag to resize"
        >
          <Box className="rz-grip" sx={{ position: 'absolute', left: '3px', top: '50%', transform: 'translateY(-50%)', width: '3px', height: '46px', borderRadius: '3px', background: '#c7cedd', transition: 'background 0.15s' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e3e6ee', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FactCheckIcon sx={{ color: '#2f57c4' }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#1f2a44', fontSize: 15, lineHeight: 1.2 }}>AI Assessment Review</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#7a8398' }}>Independent review of the current CrediArc assessment</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {report && !running && (
                  <IconButton size="small" aria-label="Re-run review" onClick={runReview} title="Run a new review">
                    <ReplayIcon sx={{ fontSize: 18, color: '#5186EC' }} />
                  </IconButton>
                )}
                <IconButton size="small" aria-label="Close" onClick={() => setOpen(false)}>
                  <CloseIcon sx={{ fontSize: 18, color: '#9aa3b8' }} />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: includeExternal ? '#1f9d55' : '#c7cedd' }} />}
                label={includeExternal ? 'Deep external research (default, slower)' : 'CrediArc data only (fast)'}
                size="small"
                onClick={() => !running && setIncludeExternal((v) => !v)}
                sx={{ cursor: running ? 'default' : 'pointer', height: 24, fontSize: 11, background: includeExternal ? '#e7f5ec' : '#eef0f5', color: includeExternal ? '#1f7a44' : '#5f6b85' }}
              />
              <Typography sx={{ fontSize: 10.5, color: '#9aa3b8' }}>Toggle then Re-run to apply</Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            {/* Progress (while running) */}
            {running && (
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7a8398', letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.5 }}>Progress</Typography>
                {LOADING_STEPS.map((s) => (
                  <StepRow key={s.key} label={s.label} state={loadedChecklist[s.key] ? 'done' : 'pending'} />
                ))}
                {REVIEW_STEPS.map((label, i) => (
                  <StepRow key={label} label={label} state={reviewIdx >= REVIEW_STEPS.length ? 'done' : i < reviewIdx ? 'done' : i === reviewIdx ? 'active' : 'pending'} />
                ))}
              </Box>
            )}

            {error && (
              <Box sx={{ p: 1.5, borderRadius: '10px', background: '#fdecea', border: '1px solid #f5c6c0' }}>
                <Typography sx={{ fontSize: 12.5, color: '#c0392b' }}>{error}</Typography>
                <Button size="small" onClick={runReview} sx={{ mt: 0.5, textTransform: 'none' }}>Try again</Button>
              </Box>
            )}

            {report && (
              <>
                {/* How the ARC Score was calculated */}
                {report.arcScore && (
                  <Box sx={{ borderRadius: '12px', border: '1px solid #e6e9f0', background: '#fff', p: 1.75, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2a44' }}>How the ARC Score was calculated</Typography>
                      {report.arcScore.score && (
                        <Chip label={`ARC ${report.arcScore.score}`} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, background: '#e8efff', color: '#2f57c4' }} />
                      )}
                    </Box>
                    {report.arcScore.summary && (
                      <Typography sx={{ fontSize: 12.5, color: '#3a4358', lineHeight: 1.6, mb: 0.75 }}>{report.arcScore.summary}</Typography>
                    )}
                    {Array.isArray(report.arcScore.drivers) && report.arcScore.drivers.length > 0 && (
                      <Box sx={{ border: '1px solid #eef0f5', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 0.6fr 0.6fr 1.4fr', background: '#f0f2f7', px: 1, py: 0.5 }}>
                          {['Component', 'Value', 'Weight', 'Effect'].map((h) => (
                            <Typography key={h} sx={{ fontSize: 10, fontWeight: 700, color: '#7a8398', textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</Typography>
                          ))}
                        </Box>
                        {report.arcScore.drivers.map((d, i) => (
                          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1.3fr 0.6fr 0.6fr 1.4fr', px: 1, py: 0.5, borderTop: '1px solid #f2f4f8', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 11.5, color: '#1f2a44', fontWeight: 500 }}>{d.component}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: '#3a4358' }}>{d.value}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: '#3a4358' }}>{d.weight}</Typography>
                            <Typography sx={{ fontSize: 11, color: d.effect === 'raised' ? '#c0392b' : d.effect === 'lowered' ? '#1f9d55' : '#7a8398' }}>
                              {d.effect}{d.note ? ` — ${d.note}` : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {Array.isArray(report.arcScore.questionSignals) && report.arcScore.questionSignals.length > 0 && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7a8398', textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.75 }}>
                          Risk-question signals ({report.arcScore.questionSignals.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {report.arcScore.questionSignals.map((q, i) => {
                            const impact = String(q.impact || '').toLowerCase();
                            const isRisk = impact.includes('risk');
                            const isFav = impact.includes('favor') || impact.includes('positive');
                            const color = isRisk ? '#c0392b' : isFav ? '#1f9d55' : '#7a8398';
                            const bg = isRisk ? '#fdecec' : isFav ? '#e9f7ef' : '#f0f2f7';
                            const label = isRisk ? 'Increases risk' : isFav ? 'Favorable' : 'Neutral';
                            const cfg = configForQuestion(q.question);
                            const eff = cfg ? scoreEffectFor(cfg.tier, cfg.boost, cfg.floor) : null;
                            return (
                              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', border: '1px solid #eef0f5', borderRadius: '8px', p: 0.9 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: color, mt: '5px', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#1f2a44' }}>{q.question}</Typography>
                                    <Chip label={label} size="small" sx={{ height: 17, fontSize: 9.5, fontWeight: 700, background: bg, color, textTransform: 'uppercase', letterSpacing: 0.3 }} />
                                  </Box>
                                  {q.finding && <Typography sx={{ fontSize: 11, color: '#3a4358', lineHeight: 1.5 }}>{q.finding}</Typography>}
                                  {q.note && <Typography sx={{ fontSize: 10.5, color: '#9aa3b8', fontStyle: 'italic', mt: 0.25 }}>{q.note}</Typography>}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                                    {eff ? (
                                      <>
                                        <Chip label={`Tier: ${cfg.tier}`} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, background: '#eef1f7', color: '#54617a', textTransform: 'capitalize' }} />
                                        <Chip label={`Weight ${cfg.boost ?? 1}`} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, background: '#eef1f7', color: '#54617a' }} />
                                        <Chip label={`Score: ${eff.label}`} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, background: eff.bg, color: eff.color }} />
                                      </>
                                    ) : (
                                      <Chip label={scoringConfig.length ? 'Context question · no score weight' : 'Weight unavailable'} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, background: '#f0f2f7', color: '#9aa3b8' }} />
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    {report.arcScore.methodologyNote && (
                      <Typography sx={{ mt: 0.75, fontSize: 10.5, fontStyle: 'italic', color: '#9aa3b8' }}>{report.arcScore.methodologyNote}</Typography>
                    )}
                  </Box>
                )}

                {/* How the Recommended Limit was calculated */}
                {report.recommendedLimit && (
                  <Box sx={{ borderRadius: '12px', border: '1px solid #e6e9f0', background: '#fff', p: 1.75, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2a44' }}>How the Recommended Limit was calculated</Typography>
                      {report.recommendedLimit.limit && (
                        <Chip label={report.recommendedLimit.limit} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, background: '#e6f4ea', color: '#1f7a43' }} />
                      )}
                    </Box>
                    {report.recommendedLimit.summary && (
                      <Typography sx={{ fontSize: 12.5, color: '#3a4358', lineHeight: 1.6, mb: 0.75 }}>{report.recommendedLimit.summary}</Typography>
                    )}
                    {Array.isArray(report.recommendedLimit.drivers) && report.recommendedLimit.drivers.length > 0 && (
                      <Box sx={{ border: '1px solid #eef0f5', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 1fr 1.2fr', background: '#f0f2f7', px: 1, py: 0.5 }}>
                          {['Factor', 'Value', 'Rule', 'Effect'].map((h) => (
                            <Typography key={h} sx={{ fontSize: 10, fontWeight: 700, color: '#7a8398', textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</Typography>
                          ))}
                        </Box>
                        {report.recommendedLimit.drivers.map((d, i) => {
                          const notApplied = /not applied|did not|n\/a|—/i.test(String(d.effect || ''));
                          return (
                            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 1fr 1.2fr', px: 1, py: 0.5, borderTop: '1px solid #f2f4f8', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: 11.5, color: '#1f2a44', fontWeight: 500 }}>{d.factor}</Typography>
                              <Typography sx={{ fontSize: 11.5, color: '#3a4358' }}>{d.value}</Typography>
                              <Typography sx={{ fontSize: 11, color: '#3a4358' }}>{d.rule}</Typography>
                              <Typography sx={{ fontSize: 11, color: notApplied ? '#9aa3b8' : '#1f7a43', fontWeight: notApplied ? 400 : 600 }}>
                                {d.effect}{d.note ? ` — ${d.note}` : ''}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {report.recommendedLimit.methodologyNote && (
                      <Typography sx={{ mt: 0.75, fontSize: 10.5, fontStyle: 'italic', color: '#9aa3b8' }}>{report.recommendedLimit.methodologyNote}</Typography>
                    )}
                  </Box>
                )}

                {/* AI Suggested Assessment (from deep research) */}
                {report.suggestedAssessment && (
                  <Box sx={{ borderRadius: '12px', border: '1px solid #d5e0fb', background: '#f5f8ff', p: 1.75, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2a44' }}>Suggested Assessment</Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handlePush}
                        disabled={pushing}
                        sx={{ textTransform: 'none', fontSize: 11.5, px: 1.5, py: 0.3, background: pushed ? '#1f9d55' : '#2f57c4', '&:hover': { background: pushed ? '#1b8a4a' : '#264bb0' } }}
                      >
                        {pushing ? 'Pushing…' : pushed ? 'Pushed ✓ — Push again' : 'Push to CrediArc'}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 0.75 }}>
                      <ValueBox label="PD" value={report.suggestedAssessment.pd} tone="accent" />
                      <ValueBox label="LGD" value={report.suggestedAssessment.lgd} tone="accent" />
                      <ValueBox label="Credit Limit" value={report.suggestedAssessment.creditLimit} tone="accent" />
                      <ValueBox label="ARC Score" value={report.suggestedAssessment.riskBand} tone="accent" />
                    </Box>
                    {report.suggestedRevenue?.valueUSD != null && report.suggestedRevenue.valueUSD !== '' && !isNaN(Number(report.suggestedRevenue.valueUSD)) && (
                      <Box sx={{ mb: 0.75, p: 1, borderRadius: '10px', background: '#eef7f0', border: '1px solid #cfe8d6' }}>
                        <Typography sx={{ fontSize: 10, color: '#1f7a48', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Suggested Revenue (CrediArc had none)</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1f7a48' }}>
                          {'$' + Number(report.suggestedRevenue.valueUSD).toLocaleString()}
                          {report.suggestedRevenue.fiscalYear ? ` · FY${report.suggestedRevenue.fiscalYear}` : ''}
                          {report.suggestedRevenue.confidence ? ` · ${report.suggestedRevenue.confidence}` : ''}
                        </Typography>
                        {report.suggestedRevenue.basis && <Typography sx={{ fontSize: 11, color: '#3a4358', mt: 0.25 }}>{report.suggestedRevenue.basis}</Typography>}
                        <Typography sx={{ fontSize: 10, fontStyle: 'italic', color: '#5f6b85', mt: 0.4 }}>On push, this revenue is applied and CrediArc re-underwrites the limit on it.</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip label={`Credit Recommendation: ${report.suggestedAssessment.recommendation || '—'}`} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, background: '#e8efff', color: '#2f57c4' }} />
                      <Chip label={`Confidence: ${report.suggestedAssessment.confidence || '—'}`} size="small" sx={{ height: 22, fontSize: 11, background: '#eef0f5', color: '#5f6b85' }} />
                    </Box>
                    {report.suggestedAssessment.rationale && (
                      <Typography sx={{ fontSize: 12, color: '#3a4358', lineHeight: 1.55 }}>{report.suggestedAssessment.rationale}</Typography>
                    )}
                    {report.suggestedAssessment.basedOnExternal && (
                      <Typography sx={{ mt: 0.75, fontSize: 10.5, fontStyle: 'italic', color: '#b7791f' }}>
                        This proposal draws on public sources and has not been incorporated into CrediArc's assessment. It is a recommendation for human review, not a change.
                      </Typography>
                    )}
                    <Typography sx={{ mt: 0.75, fontSize: 10.5, fontStyle: 'italic', color: '#7a8398' }}>
                      "Push to CrediArc" sets the suggested risk score and recommended limit as the active recommendation. CrediArc's original auto recommendation is preserved.
                    </Typography>
                  </Box>
                )}

              </>
            )}

            {/* Underwriting Copilot — chat about this assessment right inside the review */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef0f5' }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7a8398', letterSpacing: 0.4, textTransform: 'uppercase', mb: 1 }}>
                Underwriting Copilot
              </Typography>
              <UnderwritingCopilot embedded tenantName={tenantName} entityName={entityName} entity={entity} />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const Meta = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10, color: '#9aa3b8', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2a44' }}>{value}</Typography>
  </Box>
);
const SectionHeading = ({ text }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7a8398', letterSpacing: 0.4, textTransform: 'uppercase', mt: 1.5, mb: 0.75 }}>{text}</Typography>
);
const FieldLabel = ({ children }) => (
  <Typography sx={{ fontSize: 10, color: '#9aa3b8', textTransform: 'uppercase', letterSpacing: 0.3 }}>{children}</Typography>
);
const Field = ({ label, text }) => text ? (
  <Box sx={{ mt: 0.75 }}>
    <FieldLabel>{label}</FieldLabel>
    <Typography sx={{ fontSize: 12, color: '#3a4358', lineHeight: 1.55 }}>{text}</Typography>
  </Box>
) : null;
const ValueBox = ({ label, value, tone }) => (
  <Box sx={{ flex: 1, borderRadius: '8px', p: 0.9, background: tone === 'accent' ? '#eef2fb' : '#f5f6fa', border: `1px solid ${tone === 'accent' ? '#d5e0fb' : '#e6e9f0'}` }}>
    <FieldLabel>{label}</FieldLabel>
    <Typography sx={{ fontSize: 12, color: tone === 'accent' ? '#2f57c4' : '#5f6b85', fontWeight: tone === 'accent' ? 600 : 400, wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

export default AssessmentReview;
